package org.example.project.screens_driver

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ServiceDocumentViewerOverlayLayer
import org.example.project.service.ServiceDocumentViewerService
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

@Serializable
private data class SharedFolderDocument(val id: String, val name: String, val label: String = "")
@Serializable
private data class SharedFolder(val reference: String = "", val documents: List<SharedFolderDocument> = emptyList())

@Composable
fun SharedDriverDocumentFolder() {
    val token = remember { window.location.hash.removePrefix("#") }
    val json = remember { Json { ignoreUnknownKeys = true } }
    val scope = rememberCoroutineScope()
    var folder by remember { mutableStateOf<SharedFolder?>(null) }
    var unavailable by remember { mutableStateOf(false) }
    var opening by remember { mutableStateOf(false) }
    var generation by remember { mutableStateOf(0) }

    suspend fun fetchShare(action: String): org.w3c.fetch.Response {
        val options = RequestInit(method = "GET", headers = Headers().apply { append("x-document-share", token) })
        options.asDynamic().credentials = "omit"
        options.asDynamic().cache = "no-store"
        val response = window.fetch("/api/servicedocumentscreen/$action", options).await()
        check(response.ok)
        return response
    }

    fun stop() {
        generation += 1
        folder = null
        unavailable = true
        ServiceDocumentViewerService.close()
    }

    LaunchedEffect(token) {
        LocalizationManager.initialize("de")
        if (!Regex("[A-Za-z0-9_-]{43}").matches(token)) { stop(); return@LaunchedEffect }
        while (isActive && !unavailable) {
            try {
                folder = json.decodeFromString<SharedFolder>(fetchShare("share-list").text().await())
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Throwable) { stop() }
            delay(15000)
        }
    }
    DisposableEffect(Unit) { onDispose { ServiceDocumentViewerService.close() } }

    Div({ style {
        minHeight(100.vh); width(100.percent); padding(24.px); boxSizing("border-box")
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        property("background", DashboardTheme.BACKGROUND_APP_GRADIENT); color(DashboardTheme.TEXT_LIGHT)
    } }) {
        if (unavailable) Text("Freigabe beendet")
        folder?.let { current ->
            DriverDocumentFolder(
                reference = current.reference,
                documents = current.documents.map { All(id = it.id, file = it.name, bezeichnung = it.label) },
                onView = { doc ->
                    if (!opening && !unavailable) scope.launch {
                        opening = true
                        val requestGeneration = generation
                        try {
                            val blob = fetchShare("share-file/${doc.id}").blob().await()
                            if (!unavailable && generation == requestGeneration) {
                                val url = window.asDynamic().URL.createObjectURL(blob) as String
                                ServiceDocumentViewerService.show(doc.file, url)
                            }
                        } catch (cancelled: CancellationException) { throw cancelled }
                        catch (_: Throwable) { stop() }
                        finally { opening = false }
                    }
                }
            )
        }
    }
    ServiceDocumentViewerOverlayLayer()
}
